import * as PrimitiveRadixDialog from '@radix-ui/react-dialog';
import { useCombinedRefs } from '@visiba/react-std';
import { AnimatePresence, motion, PanInfo, useDragControls } from 'framer-motion';
import { PointerEvent, ReactNode, Ref, RefObject, UIEvent, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { useText } from '../../utils/hooks/use_text';
import { restrictedZIndexes } from '../../utils/restricted_z_indexes';
import { IconButton } from '../icon_button';
import { useCellulaProviderContext } from '../provider';
import { SheetContext } from './_sheet.context';
import { SheetBackgroundOverlay } from './_sheet_background_overlay.component';

const StyledContent = styled(motion.div)`
	--_padding: ${coreTokens.spacing.x1};
	--_inner-r: ${coreTokens.borderRadius.medium};
	--_outer-r: calc(var(--_inner-r) + var(--_padding));
	--_background-color: #fff;
	--_color: #000;

	width: 100%;
	/* Gives a 30px margin from the top. Values like "vh" are not consistent across devices. */
	height: calc(100% - 30px);
	position: fixed;
	bottom: 0;
	left: 0;
	padding: var(--_outer-r) 0;
	box-sizing: border-box;
	/* z-index: 1; */
	/* Needs to use a high z-index to have it appear on top of other elements */
	z-index: ${restrictedZIndexes.DANGER};

	/* Alter the spaces the sheet will take when at desktop. */
	@media screen and (min-width: 500px) {
		height: calc(100% - 40px);
	}

	/* A pseudo element is used as the content filling, it comes with the benefit to extend the
	   height without affecting the layout. */
	&::before {
		content: '';
		display: block;
		background: var(--_background-color);
		border-radius: var(--_outer-r);
		/* Needs to be x bigger so the sheet may go under the viewport. It's for the cases where
			 the user drags the sheet upwards, will look wonky without it. */
		height: calc(100vh * 2);

		z-index: -1;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;

		box-shadow: 0 -5px 25px 7px rgb(0 0 0 / 20%);
	}

	&[data-variant='half'],
	&[data-variant='half-restricted'] {
		height: calc(50% + 30px);
	}

	&[data-variant='half-restricted'] {
		overflow: hidden;
		border-radius: calc(var(--_outer-r) * 2);
		bottom: 10px;
		max-width: 600px;
		max-height: 540px;
	}

	&[data-variant='auto'],
	&[data-variant='auto-restricted'] {
		height: var(--_child-height);
		max-height: calc(100% - 30px);
	}
`;

const StyledScroller = styled.div`
	/* Padding out the bottom is important, else the bottom scroll area can't be reached. */
	height: calc(100% + 60px);

	position: absolute;
	top: 0;
	left: 0;
	right: 0;

	overflow-y: auto;
	overflow-x: hidden;
	/* Chrome and Firefox has an blur bleed issue which is super ugly, we'll use this for those browsers instead */
	mask-image: linear-gradient(to bottom, transparent 40px, #000 55px);

	/* There to hide the scrollbar to overflow up the corners. */
	border-top-left-radius: var(--_outer-r);
	border-top-right-radius: var(--_outer-r);

	/* Makes any scrolling impossible, its intention is to shave off any wonky edge cases that
	   may cause interference with the dragging. */
	&.removeUserEvents {
		pointer-events: none;
	}
`;

const StyledScrollerPadding = styled.section`
	margin: 48px 0 96px;
	min-height: calc(100% - 48px - 96px);
	width: 100%;
	display: flex;
	flex-direction: column;
	outline: none;
`;

const StyledTopBar = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;

	width: 100%;
	height: 48px;

	position: absolute;
	top: 0;
	left: 0;
	right: 0;

	border-top-left-radius: var(--_outer-r);
	border-top-right-radius: var(--_outer-r);
	padding: var(--_padding) var(--_padding) var(--_padding) calc(var(--_padding) * 2);
	box-sizing: border-box;

	/* Prevents accidental text selection when pulling down the Sheet. */
	user-select: none;

	/* Only use this for Safari. */
	/* backdrop-filter: saturate(180%) blur(30px); */

	background-color: rgba(255, 255, 255, 0.72);
	transition: background-color 150ms;

	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: -1px;
		height: 1px;
		background: rgba(0, 0, 0, 0.16);
		opacity: 0;
	}

	&.show {
		&::after {
			opacity: 1;
		}
	}
`;

// These values are chosen based on "feel good" intuition.
// High velocity means that the user was dragging down at quite the speed -> so probably wants to close it.
// And to avoid the user to accidentally "swipe close it" by just touching a few points, we have at least
// a 35 pixel minimum from the point of impact.
const CLOSE_VELOCITY_THRESHOLD = 900;
const CLOSE_DRAGGED_PX_THRESHOLD = 35;

export interface SheetContentProps {
	children: ReactNode;
}

export function SheetContent({ children }: SheetContentProps, ref: Ref<HTMLDivElement>): JSX.Element {
	const {
		id,
		setIsOpen,
		isOpen,
		label,
		variant,
		onOpenAutoFocus,
		onCloseAutoFocus,
		onEscapeKeyDown,
		onPointerDownOutside,
		onInteractOutside,
		onCloseAnimationEnd,
		// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	} = useContext(SheetContext);
	const labelId = `${id}-label`;
	const isFullSized = variant.includes('full');
	const isRestricted = variant.includes('restricted');
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const { portalElement } = useCellulaProviderContext();

	// To enable user's ability to drag the container, these are:
	// 1. When user origin touch point does not contain the scroll area.
	// 2. When user has not scrolled within the scroll area.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const dragControls = useDragControls();

	// The "dragControls.start" should only be invoked once. This state ensures that is indeed the case.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const [hasStartedDragging, setHasStartedDragging] = useState(false);

	// This is a hack. The velocity received on the drag end event is not accurate.
	// So we need to store the last tick of the PanInfo and check towards that instead.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const panInfoCache = useRef<PanInfo | null>(null);

	// See list item #1 in the dragControls comment.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const scrollRef = useRef<HTMLDivElement>(null);

	// To better determine the height of the sheet we need to check the height of the children,
	// to get the ref of where the height should go we need to do the combined ref thing the
	// ref may still work outside of this component.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const containerRef = useRef<HTMLDivElement>(null);
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const combinedRef = useCombinedRefs(ref, containerRef);

	// Sometimes we need to prevent the dragging until touch ends.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const disableDraggingUntilEndOfTouch = useRef(false);

	// To store positional value to determine directional drag. Without it some unwanted
	// "dragControls.start" could occur. making the experience really poor.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const pointOfImpact = useRef<number | null>(null);

	// To store if the scroll area has been scrolled in.
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const hasScrolledFor = useRef(0);

	// Static Texts
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	// ===== HELPER UTILS =====

	function allowUserToDrag(eventOrigin: PointerEvent): void {
		if (disableDraggingUntilEndOfTouch.current) return;

		// Text selection is checked to not interfere with the user's input.
		if (getSelection()?.toString()) {
			disableDraggingUntilEndOfTouch.current = true;

			return;
		}
		// User is interaction with an input, let's not interfere then.
		if (eventOrigin.target instanceof HTMLInputElement || eventOrigin.target instanceof HTMLTextAreaElement) {
			disableDraggingUntilEndOfTouch.current = true;

			return;
		}

		if (!hasStartedDragging) {
			dragControls.start(eventOrigin);
			setHasStartedDragging(true);
		}
	}

	// Method to check if the user has moved container in a way that looks like a wanted closing action.
	function hasReachedAnExitThreshold(y: number, velocity: number | null): boolean {
		if (isRestricted) return false;
		if (velocity == null) return false;
		if (velocity < CLOSE_VELOCITY_THRESHOLD) return false;

		return y > CLOSE_DRAGGED_PX_THRESHOLD;
	}

	function reset(): void {
		pointOfImpact.current = null;
		setHasStartedDragging(false);
		disableDraggingUntilEndOfTouch.current = false;
	}

	// ===== HANDLERS =====

	function handlePointerUp(_: PointerEvent<HTMLDivElement>): void {
		// The user is no longer interaction with the container, so we preforms some resets here.
		reset();
	}

	function handlePointerMove(event: PointerEvent<HTMLDivElement>): void {
		if (pointOfImpact.current == null) return;

		// "1" is being represented as down and "0" up.
		function getDragDirection(): number | null {
			if (event.screenY > pointOfImpact.current!) {
				return 1;
			}
			if (event.screenY < pointOfImpact.current!) {
				return 0;
			}
			return null;
		}
		const dragDirection = getDragDirection();
		if (dragDirection === 1 && hasScrolledFor.current === 0) {
			allowUserToDrag(event);
		}
	}

	function handlePointerDown(event: PointerEvent<HTMLDivElement>): void {
		pointOfImpact.current = event.screenY;

		// Checks if user touches the scrollable area, important that it does not allows dragging if.
		if (scrollRef.current?.contains(event.target as HTMLElement)) {
			return;
		}

		allowUserToDrag(event);
	}

	function handleDragEnd(_: MouseEvent, __: PanInfo): void {
		// The user is no longer interaction with the container, so we preforms some resets here.
		reset();

		if (panInfoCache.current == null) return;

		// Closes the sheet if the exit threshold was indeed reached.
		const result = hasReachedAnExitThreshold(panInfoCache.current.offset.y, panInfoCache.current.velocity.y);
		setIsOpen(!result);
		panInfoCache.current = null;
	}

	function handleDrag(_: MouseEvent, panInfo: PanInfo): void {
		panInfoCache.current = panInfo;
	}

	function handleScroll(event: UIEvent<HTMLDivElement>): void {
		hasScrolledFor.current = event.currentTarget.scrollTop;
	}

	return (
		<AnimatePresence onExitComplete={onCloseAnimationEnd}>
			{isOpen ? (
				<PrimitiveRadixDialog.Portal container={portalElement} forceMount>
					<SheetBackgroundOverlay />
					<PrimitiveRadixDialog.Content
						asChild
						forceMount
						onCloseAutoFocus={onCloseAutoFocus}
						onOpenAutoFocus={onOpenAutoFocus}
						onEscapeKeyDown={onEscapeKeyDown}
						onPointerDownOutside={onPointerDownOutside}
						onInteractOutside={onInteractOutside}
					>
						<StyledContent
							data-variant={variant}
							ref={combinedRef}
							onDrag={handleDrag}
							onDragEnd={handleDragEnd}
							onPointerMove={handlePointerMove}
							onPointerDown={handlePointerDown}
							onPointerUp={handlePointerUp}
							drag='y'
							dragControls={dragControls}
							dragListener={false}
							dragSnapToOrigin
							dragConstraints={{
								top: 0,
								bottom: isRestricted ? 0 : undefined,
							}}
							transition={{
								type: 'spring',
								// the 3 differences when not fullSized does make a difference with smoothing out the transition.
								damping: isFullSized ? 23 : 20,
								stiffness: 200,
							}}
							initial={{ y: '100%' }}
							animate={{ y: 0 }}
							// Using 150px more to account for the box-shadow and iOS's address bar. This could be solved in
							// different way but this is just an easy way to do it.
							exit={{
								y: 'calc(100% + 150px)',
							}}
						>
							<ScrollableArea
								scrollRef={scrollRef}
								containerRef={combinedRef}
								hasStartedDragging={hasStartedDragging}
								handleScroll={handleScroll}
								labelId={labelId}
							>
								{children}
							</ScrollableArea>

							{/* It's intentional that this is at the bottom. Semantically it should be the last thing the user can tab to. */}
							<StyledTopBar>
								{
									label ? (
										<div id={labelId} aria-hidden='true'>
											{label}
										</div>
									) : (
										<div />
									) /* Uses a an empty div to make space between work */
								}
								<PrimitiveRadixDialog.Close asChild>
									<IconButton variant='neutral' size='medium' icon='x' text={staticTexts('Common.Close')} />
								</PrimitiveRadixDialog.Close>
							</StyledTopBar>
						</StyledContent>
					</PrimitiveRadixDialog.Content>
				</PrimitiveRadixDialog.Portal>
			) : null}
		</AnimatePresence>
	);
}

function ScrollableArea(props: {
	scrollRef: RefObject<HTMLDivElement>;
	containerRef: RefObject<HTMLDivElement>;
	hasStartedDragging: boolean;
	handleScroll: (event: UIEvent<HTMLDivElement>) => void;
	children: React.ReactNode;
	labelId: string;
}): JSX.Element {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(
		function whenChildrenChangesUpdateHeight() {
			const height = ref.current?.getBoundingClientRect().height ?? 0;

			props.containerRef.current?.style.setProperty('--_child-height', `${height + 48 + 40}px`);
		},
		[props.containerRef],
	);

	return (
		<StyledScroller
			ref={props.scrollRef}
			className={props.hasStartedDragging ? 'removeUserEvents' : undefined}
			onScroll={props.handleScroll}
		>
			<StyledScrollerPadding ref={ref} tabIndex={0} aria-labelledby={props.labelId}>
				{props.children}
			</StyledScrollerPadding>
		</StyledScroller>
	);
}
